import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from 'src/redux/store';
import { WeblabState } from 'src/types/state/weblab';
import { WeblabId, WeblabTreatment } from 'src/constants/weblab';

export interface WeblabSliceState {
  byId: Record<string, WeblabState>
}

export const initialState: WeblabSliceState = {
  byId: {},
};

export const weblabsSlice = createSlice({
  name: 'weblabs',
  initialState,
  reducers: {
    setWeblabs: (
      state,
      action: PayloadAction<Record<string, string>>,
    ) => {
      const buildWeblabEntitiesById = (weblabs: Record<WeblabId, string>) => {
        const weblabEntitiesById: Record<string, WeblabState> = {};
        Object.entries(weblabs).forEach(([weblabId, treatment]) => {
          weblabEntitiesById[weblabId] = {
            weblabId: weblabId as WeblabId,
            treatment: treatment as WeblabTreatment,
          };
        });
        return weblabEntitiesById;
      };
      state.byId = buildWeblabEntitiesById(action.payload);
    },
  },
});

/**
 * Base selector that retrieves a specific weblab's state from the Redux store.
 * This selector is memoized and will only recompute when the specific weblab
 * being selected changes, not when other weblabs are updated.
 *
 * @param state - The Redux application state
 * @param weblabId - The ID of the weblab to retrieve
 * @returns The weblab state object if found, undefined otherwise
 *
 * @example
 * ```typescript
 * const weblab = useSelector(state => selectWeblab(state, 'MY_WEBLAB'));
 * if (weblab) {
 *   // Access weblab properties like treatment, etc.
 * }
 * ```
 */
export const selectWeblab = createSelector(
  [
    (state: AppState) => state.weblabs.byId,
    (state: AppState, weblabId: WeblabId) => weblabId,
  ],
  (weblabsById, weblabId) => weblabsById[weblabId],
);

/**
 * Selector that determines if a specific weblab is in the T1 treatment group.
 * Built on top of selectWeblab for efficient memoization - only recalculates
 * when the underlying weblab data changes.
 *
 * @param state - The Redux application state
 * @param weblabId - The ID of the weblab to check
 * @returns true if the weblab exists and is in T1 treatment, false otherwise
 *
 * @example
 * ```typescript
 * const isT1 = useSelector(state => selectIsWeblabInT1(state, 'MY_WEBLAB'));
 * if (isT1) {
 *   // Show T1 treatment features
 * }
 * ```
 *
 * @see WeblabTreatment for possible treatment values
 * @see selectWeblab for the underlying weblab data selector
 */
export const selectIsWeblabInT1 = createSelector(
  [selectWeblab],
  (weblab) => {
    return weblab?.treatment === WeblabTreatment.T1;
  },
);

export const weblabsActions = weblabsSlice.actions;
